import * as React from 'react';
import { useSelector } from 'react-redux';

import { ETAZHI_BLACK_FRIDAY_CAROUSEL_OPTIONS } from './constants';
import {
  trackEtazhiLinkButtonClick,
  trackEtazhiLogoClick,
  trackEtazhiPromoOffersShown,
  trackOfferClick,
} from './tracking';
import { Carousel as EtazhiBlackFridayPromoOffersCarousel } from '../../../packages/Carousel/Carousel';
import { EtazhiBlackFridayCard } from 'shared/components/EtazhiBlackFridayCard';
import { EtazhiBlackFridayPromoOfferCard } from 'shared/components/EtazhiBlackFridayPromoOfferCard';
import { EtazhiBlackFridayPromoOffersLayout } from 'shared/components/EtazhiBlackFridayPromoOffersLayout';
import {
  selectEtazhiBlackFridayPromoOffers,
  selectEtazhiBlackFridayPromoOffersData,
  selectEtazhiBlackFridaySerpLink,
} from 'shared/selectors/etazhiBlackFriday';
import { selectJsonQuery } from 'shared/selectors/results';

import type { Swiper } from 'swiper';

/**
 * Код появился к проекту "Чёрная пятница" для компании "Этажи".
 * @todo: удалить в задаче CD-250843
 */
export function EtazhiBlackFridayPromoOffers() {
  const promoOffers = useSelector(selectEtazhiBlackFridayPromoOffers);
  const promoOffersData = useSelector(selectEtazhiBlackFridayPromoOffersData);
  const serpLink = useSelector(selectEtazhiBlackFridaySerpLink);
  const jsonQuery = useSelector(selectJsonQuery);

  const onOfferClick = React.useCallback((offerId: number, position: number) => {
    trackOfferClick(offerId, position);
  }, []);

  const onEtazhiLogoClick = React.useCallback(() => {
    trackEtazhiLogoClick(jsonQuery);
  }, [jsonQuery]);

  const onEtazhiLinkButtonClick = React.useCallback(() => {
    trackEtazhiLinkButtonClick(jsonQuery);
  }, [jsonQuery]);

  const onPromoOffersShown = React.useCallback(() => {
    if (promoOffers && promoOffers.length) {
      /**
       * Офферы, которые видны в слайдере полки при первой отрисовке карусели.
       */
      const offersToTrack = promoOffers.slice(0, ETAZHI_BLACK_FRIDAY_CAROUSEL_OPTIONS.slidesPerView);

      trackEtazhiPromoOffersShown(jsonQuery, offersToTrack, 0);
    }
  }, [jsonQuery, promoOffers]);

  const onSlideChange = React.useCallback(
    (swiper: Swiper) => {
      if (promoOffers && promoOffers.length) {
        const { activeIndex } = swiper;

        /**
         * Офферы, которые видны в слайдере полки при клике на стрелку вперед и назад.
         */
        const offersToTrack = promoOffers.slice(
          activeIndex,
          activeIndex + ETAZHI_BLACK_FRIDAY_CAROUSEL_OPTIONS.slidesPerView,
        );

        trackEtazhiPromoOffersShown(jsonQuery, offersToTrack, activeIndex);
      }
    },
    [promoOffers, jsonQuery],
  );

  const slides = React.useMemo(
    () =>
      promoOffers?.length
        ? promoOffers.map((offer, index) => (
            <EtazhiBlackFridayPromoOfferCard
              key={offer.id}
              offer={offer}
              onOfferClick={() => onOfferClick(offer.id, index)}
            />
          ))
        : [],
    [promoOffers, onOfferClick],
  );

  const carousel = React.useMemo(
    () => (
      <EtazhiBlackFridayPromoOffersLayout
        onPromoOffersShown={onPromoOffersShown}
        offersViewed={promoOffers ? promoOffers?.length < ETAZHI_BLACK_FRIDAY_CAROUSEL_OPTIONS.slidesPerView : true}
        card={
          <EtazhiBlackFridayCard
            onLogoClick={onEtazhiLogoClick}
            onButtonClick={onEtazhiLinkButtonClick}
            promoOffersData={promoOffersData}
            serpLink={serpLink}
          />
        }
        carousel={
          <EtazhiBlackFridayPromoOffersCarousel {...ETAZHI_BLACK_FRIDAY_CAROUSEL_OPTIONS} onSlideChange={onSlideChange}>
            {slides}
          </EtazhiBlackFridayPromoOffersCarousel>
        }
      />
    ),
    [
      promoOffers,
      slides,
      promoOffersData,
      serpLink,
      onPromoOffersShown,
      onEtazhiLogoClick,
      onEtazhiLinkButtonClick,
      onSlideChange,
    ],
  );

  if (!promoOffers) {
    return null;
  }

  return carousel;
}
